import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { cva } from 'class-variance-authority';
import { Check, Lock } from 'lucide-react';

import {
  type CustomerListResponseOutputItemsItemCounts,
  type SubmissionDetailResponseOutputShelvesItem,
} from '@/api/generated';
import AnnotationBadges from '@/components/blocks/shared/annotation-badges';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription } from '@/components/ui/card';
import { getLabeledCount } from '@/lib/label-counts';
import { cn } from '@/lib/utils';

interface ShelveCardProps {
  shelf: SubmissionDetailResponseOutputShelvesItem;
  customerId: string;
  submissionId: string;
}

export const isShelfCompleted = (
  shelf: SubmissionDetailResponseOutputShelvesItem
) => shelf.status === 'annotation_completed';

// The explicit annotator-set shelf status wins. 'complete' is never derived
// from detection counts: every shelf must be reviewed and explicitly
// completed by a human before it reads as complete. Shelves never saved only
// distinguish pending from in-progress by whether any detection has a label.
const getShelfStatus = (
  shelf: SubmissionDetailResponseOutputShelvesItem
): 'pending' | 'inProgress' | 'complete' => {
  if (isShelfCompleted(shelf)) {
    return 'complete';
  }

  if (shelf.status === 'annotation_in_progress') {
    return 'inProgress';
  }

  return getLabeledCount(shelf.counts) > 0 ? 'inProgress' : 'pending';
};

const statusBadgeVariants = cva('absolute top-2 right-2', {
  variants: {
    status: {
      pending:
        'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
      inProgress:
        'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
      complete:
        'border-transparent bg-success text-success-foreground hover:bg-success/80',
    },
  },
  defaultVariants: { status: 'pending' },
});

export const ShelveCard: FC<ShelveCardProps> = ({
  shelf,
  customerId,
  submissionId,
}: ShelveCardProps) => {
  const { t } = useTranslation();
  const status = getShelfStatus(shelf);
  const completed = status === 'complete';

  return (
    <Link
      to='/app/shelves/$customerId/$submissionId/$shelveId'
      params={{
        customerId,
        submissionId,
        shelveId: String(shelf.id),
      }}>
      <Card
        data-testid='shelf-card'
        data-completed={completed}
        className={cn(
          'group gap-0 transition-shadow duration-200 hover:shadow-md',
          completed && 'border-success border-2'
        )}>
        <CardContent className='relative aspect-[4/3] overflow-hidden bg-muted'>
          <img
            className='size-full object-cover transition-transform duration-200 group-hover:scale-[var(--image-scale)]'
            alt=''
            src={shelf.imagePath}
            loading='lazy'
            decoding='async'
          />
          {shelf.locked && (
            <div className='bg-background/70 absolute inset-0 flex flex-col items-center justify-center gap-2 backdrop-blur-xs'>
              <Lock className='size-6' />
              <span className='text-sm font-medium'>{t('common.locked')}</span>
            </div>
          )}
          {completed ? (
            <div
              className='bg-success text-success-foreground absolute top-0 right-0 size-12 [clip-path:polygon(0_0,100%_0,100%_100%)]'
              aria-label={t('shelves.status.complete')}>
              <Check
                className='absolute top-1 right-1 size-5'
                strokeWidth={3}
              />
            </div>
          ) : (
            <Badge className={statusBadgeVariants({ status })}>
              {t(`shelves.status.${status}`)}
            </Badge>
          )}
        </CardContent>
        <CardDescription
          className={cn(
            'flex items-start justify-between gap-3 px-3 pt-2 pb-3',
            completed && 'bg-success/10'
          )}>
          <span className='font-mono text-xs leading-5'>#{shelf.id}</span>
          <AnnotationBadges
            className='justify-end'
            counts={shelf.counts as CustomerListResponseOutputItemsItemCounts}
          />
        </CardDescription>
      </Card>
    </Link>
  );
};
